import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Quiz archetype — wraps "Help me decide" scoring. Reads the global
// quiz pool from `adapters.quizPool`. Each answer's `weights` map
// contributes to a plan's score; the highest-scoring plan is folded
// into the payload as `planId` (configurable via planIdPath). Behavior
// ported from apps/portal/src/signup/steps/QuizStep.tsx.
//
// Control archetype — owns its own advance via the answer-pick flow.
import { useEffect, useMemo, useState } from 'react';
import { s } from './common.js';
export function computeScores(questions, picks) {
    const totals = {};
    for (const q of questions) {
        const idx = picks[q.id];
        if (idx == null)
            continue;
        const ans = q.answers[idx];
        if (!ans)
            continue;
        for (const [planId, weight] of Object.entries(ans.weights)) {
            totals[planId] = (totals[planId] ?? 0) + weight;
        }
    }
    return totals;
}
export function topPlan(questions, scores) {
    const order = [];
    const seen = new Set();
    for (const q of questions) {
        for (const a of q.answers) {
            for (const planId of Object.keys(a.weights)) {
                if (!seen.has(planId)) {
                    seen.add(planId);
                    order.push(planId);
                }
            }
        }
    }
    let best = null;
    let bestScore = -Infinity;
    for (const planId of order) {
        const sc = scores[planId] ?? 0;
        if (sc > bestScore) {
            bestScore = sc;
            best = planId;
        }
    }
    return bestScore > 0 ? best : null;
}
export function QuizComponent(props) {
    const { config, adapters, advance, back, isAdvancing } = props;
    const questions = adapters.quizPool ?? [];
    const planIdPath = s(config, 'planIdPath', 'planId');
    const answersPath = s(config, 'answersPath', 'quizAnswers');
    const [picks, setPicks] = useState({});
    const [questionIdx, setQuestionIdx] = useState(0);
    useEffect(() => {
        if (questions.length === 0 && !isAdvancing) {
            void advance({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const scores = useMemo(() => computeScores(questions, picks), [questions, picks]);
    const recommended = useMemo(() => topPlan(questions, scores), [questions, scores]);
    if (questions.length === 0) {
        return _jsx("div", { className: "text-sm text-[var(--bcbb-text-muted)]", children: "Loading\u2026" });
    }
    const question = questions[questionIdx];
    if (!question) {
        return _jsx("div", { className: "text-sm text-[var(--bcbb-text-muted)]", children: "All set \u2014 moving on\u2026" });
    }
    function pick(answerIdx) {
        const nextPicks = { ...picks, [question.id]: answerIdx };
        setPicks(nextPicks);
        const isLast = questionIdx === questions.length - 1;
        if (isLast) {
            const quizAnswers = {};
            for (const q of questions) {
                const idx = nextPicks[q.id];
                if (idx == null)
                    continue;
                const ans = q.answers[idx];
                if (ans)
                    quizAnswers[q.id] = ans.label;
            }
            const finalScores = computeScores(questions, nextPicks);
            const top = topPlan(questions, finalScores);
            const patch = { [answersPath]: quizAnswers };
            if (top)
                patch[planIdPath] = top;
            void advance(patch);
        }
        else {
            setQuestionIdx(questionIdx + 1);
        }
    }
    return (_jsxs("div", { className: "flex flex-col gap-5", children: [_jsxs("div", { className: "flex items-center justify-between text-xs text-[var(--bcbb-text-muted)]", children: [_jsxs("span", { children: ["Question ", questionIdx + 1, " of ", questions.length] }), recommended && (_jsxs("span", { className: "rounded-full bg-[var(--bcbb-surface-2,#f1f2f4)] px-2 py-0.5", children: ["Current recommendation: ", recommended] }))] }), _jsx("h2", { className: "m-0 text-xl font-semibold", children: question.prompt }), _jsx("div", { className: "flex flex-col gap-2", children: question.answers.map((a, idx) => (_jsx("button", { type: "button", onClick: () => pick(idx), disabled: isAdvancing, className: "rounded-xl border-2 border-[var(--bcbb-border)] bg-white px-4 py-3 text-left text-sm font-medium disabled:opacity-60", children: a.label }, `${question.id}-${idx}`))) }), _jsx("div", { className: "mt-2 flex items-center justify-between", children: _jsx("button", { type: "button", onClick: back, disabled: isAdvancing, className: "rounded-md border bg-white px-4 py-2 text-sm font-medium", style: { borderColor: 'var(--bcbb-border)' }, children: "Back" }) })] }));
}
export const QuizArchetype = {
    name: 'Quiz',
    label: 'Quiz (Help me decide)',
    description: 'Renders quiz questions from the global pool; scores into a plan recommendation.',
    component: QuizComponent,
    isFormArchetype: false,
    fields: {
        planIdPath: { kind: 'text', label: 'Plan id payload key', defaultValue: 'planId' },
        answersPath: { kind: 'text', label: 'Answers payload key', defaultValue: 'quizAnswers' },
    },
    defaultConfig: { planIdPath: 'planId', answersPath: 'quizAnswers' },
};
